// Sound Effects Utility for Space Theme
// Uses Web Audio API to generate simple tones
// Can be extended with actual audio files later

class SoundEffects {
    constructor() {
        this.enabled = localStorage.getItem('soundEnabled') === 'true' || false;
        this.audioContext = null;
        this.masterGain = null;
        this.initAudioContext();
    }

    initAudioContext() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.audioContext.createGain();
            this.masterGain.connect(this.audioContext.destination);
            this.masterGain.gain.value = 0.3; // Master volume
        } catch (e) {
            console.warn('Web Audio API not supported:', e);
        }
    }

    toggle() {
        this.enabled = !this.enabled;
        localStorage.setItem('soundEnabled', this.enabled.toString());
        return this.enabled;
    }

    isEnabled() {
        return this.enabled;
    }

    // Play a tone with given frequency and duration
    playTone(frequency, duration, type = 'sine') {
        if (!this.enabled || !this.audioContext) return;

        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.frequency.value = frequency;
        oscillator.type = type; // sine, square, triangle, sawtooth

        // Envelope
        const now = this.audioContext.currentTime;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.5, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

        oscillator.start(now);
        oscillator.stop(now + duration);
    }

    // Whoosh sound - for rocket movement or transitions
    whoosh() {
        if (!this.enabled || !this.audioContext) return;

        const now = this.audioContext.currentTime;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(200, now);
        oscillator.frequency.exponentialRampToValueAtTime(50, now + 0.3);

        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

        oscillator.start(now);
        oscillator.stop(now + 0.3);
    }

    // Beep sound - for field completion
    beep() {
        if (!this.enabled || !this.audioContext) return;
        this.playTone(800, 0.1, 'sine');
    }

    // Success chime - multiple ascending tones
    successChime() {
        if (!this.enabled || !this.audioContext) return;

        const notes = [523.25, 659.25, 783.99]; // C, E, G
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.playTone(freq, 0.2, 'sine');
            }, i * 100);
        });
    }

    // Error buzz - descending harsh tone
    errorBuzz() {
        if (!this.enabled || !this.audioContext) return;

        const now = this.audioContext.currentTime;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(200, now);
        oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.2);

        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

        oscillator.start(now);
        oscillator.stop(now + 0.2);
    }

    // Blast off sound - rocket launch
    blastOff() {
        if (!this.enabled || !this.audioContext) return;

        const now = this.audioContext.currentTime;

        // Low rumble
        const rumble = this.audioContext.createOscillator();
        const rumbleGain = this.audioContext.createGain();
        rumble.connect(rumbleGain);
        rumbleGain.connect(this.masterGain);
        rumble.type = 'sawtooth';
        rumble.frequency.setValueAtTime(40, now);
        rumble.frequency.linearRampToValueAtTime(60, now + 1.5);
        rumbleGain.gain.setValueAtTime(0.4, now);
        rumbleGain.gain.exponentialRampToValueAtTime(0.01, now + 1.5);
        rumble.start(now);
        rumble.stop(now + 1.5);

        // High whoosh
        setTimeout(() => {
            const whoosh = this.audioContext.createOscillator();
            const whooshGain = this.audioContext.createGain();
            whoosh.connect(whooshGain);
            whooshGain.connect(this.masterGain);
            whoosh.type = 'sine';
            whoosh.frequency.setValueAtTime(400, this.audioContext.currentTime);
            whoosh.frequency.exponentialRampToValueAtTime(1000, this.audioContext.currentTime + 0.8);
            whooshGain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
            whooshGain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.8);
            whoosh.start(this.audioContext.currentTime);
            whoosh.stop(this.audioContext.currentTime + 0.8);
        }, 700);
    }

    // Telescope scan sound - rotating beam
    telescopeScan() {
        if (!this.enabled || !this.audioContext) return;

        const now = this.audioContext.currentTime;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(300, now);
        oscillator.frequency.linearRampToValueAtTime(400, now + 0.5);
        oscillator.frequency.linearRampToValueAtTime(300, now + 1);

        gainNode.gain.setValueAtTime(0.2, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 1);

        oscillator.start(now);
        oscillator.stop(now + 1);
    }

    // Lock on sound - target acquired
    lockOn() {
        if (!this.enabled || !this.audioContext) return;

        const now = this.audioContext.currentTime;

        // Quick beeps
        for (let i = 0; i < 3; i++) {
            const beep = this.audioContext.createOscillator();
            const beepGain = this.audioContext.createGain();
            beep.connect(beepGain);
            beepGain.connect(this.masterGain);
            beep.frequency.value = 600;
            beepGain.gain.setValueAtTime(0.3, now + i * 0.15);
            beepGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.1);
            beep.start(now + i * 0.15);
            beep.stop(now + i * 0.15 + 0.1);
        }

        // Confirmation tone
        setTimeout(() => {
            this.playTone(800, 0.3, 'triangle');
        }, 500);
    }

    // Button click sound
    click() {
        if (!this.enabled || !this.audioContext) return;
        this.playTone(400, 0.05, 'square');
    }
}

// Export singleton instance
const soundEffects = new SoundEffects();
export default soundEffects;
